import type { IResolvedProductTag } from "@/utils/functions/product-tags";
import SvgBadge from "./SvgBadge";

interface Props {
  tag: IResolvedProductTag;
  /** Used to build a DOM id that is unique per card. */
  productId: number | string;
  large: boolean;
}

/**
 * One theme-configured catalog tag, rendered as the same ribbon badge used by the
 * NEW / discount / out-of-stock badges.
 *
 * The configured colour is written inline as `--product-tag-theme-bg`; the stylesheet
 * reads `--product-tag-bg`, which nothing sets by default. That leaves the override hook
 * free, so a merchant can restyle a tag from Store Settings > Custom Scripts > Header at
 * any specificity and without `!important`:
 *
 *   #product-tag-987-12      { --product-tag-bg: #000; }   one tag on one product
 *   .product-tag-badge-12    { --product-tag-bg: #000; }   that tag everywhere
 *   [data-tag-name="Sale"]   { --product-tag-bg: #000; }   by name
 *   .product-tag-badge       { ... }                       every tag badge
 */
const ProductTagBadge: React.FC<Props> = ({ tag, productId, large }) => (
  <div
    id={`product-tag-${productId}-${tag.id}`}
    className={`p-0 product-tag-badge product-tag-badge-${tag.id}`}
    data-tag-id={tag.id}
    data-tag-name={tag.name}
    style={
      {
        "--product-tag-theme-bg": tag.bgColor,
        "--product-tag-theme-color": tag.textColor,
      } as React.CSSProperties
    }
  >
    <SvgBadge
      text={tag.name}
      large={large}
      fillClassName="fill-product-tag"
      textClassName="fill-product-tag-text"
    />
  </div>
);

export default ProductTagBadge;
